import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loading from "app/storybookComponents/Loading";
import { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { TIMEZONE_OPTIONS, TIMEZONE_SECTIONS } from "utils/constants";
import { useDebounceCallback } from "utils/hooks";
import { getFilteredSectionOptions } from "./helpers";

interface Props {
  selectedValue?: string;
  className?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  onTzCodeSelect: (tzCode: string) => void;
}

const TimezoneDropdown = ({
  selectedValue,
  className,
  disabled,
  searchPlaceholder = "Search Timezone",
  onTzCodeSelect,
}: Readonly<Props>) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [trueSearchTerm, setTrueSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const getDropdownClassName = () => {
    let dropdownClassName = "searchable-dropdown timezone-dropdown";

    if (className) {
      dropdownClassName += ` ${className}`;
    }
    return dropdownClassName;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    onDebounceSearch(value);
  };

  const onDebounceSearch = useDebounceCallback((value: string) => {
    setTrueSearchTerm(value);
  }, 300);

  const handleToggle = (isOpen: boolean) => {
    setShowDropdown(isOpen);
    if (!isOpen) {
      setSearchTerm("");
      setTrueSearchTerm("");
    }
  };

  const getSearchableInput = () => {
    return (
      <div className="timezone-dropdown-search-input-container search-input-container">
        <div className="timezone-dropdown-search-input-icon-container">
          <FontAwesomeIcon
            icon={faSearch}
            className="timezone-dropdown-search-input-icon"
          />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder={searchPlaceholder}
          style={{ paddingLeft: "35px", width: "100%", borderRadius: "4px" }}
          className="timezone-dropdown-search-input"
        />
      </div>
    );
  };

  const dropdownOptions = useMemo(() => {
    const sections: JSX.Element[] = [];
    let isEmpty = true;
    Object.entries(TIMEZONE_SECTIONS).forEach(([sectionName, tzCodes]) => {
      const sectionOptions = getFilteredSectionOptions(
        tzCodes,
        TIMEZONE_OPTIONS,
        sectionName,
        trueSearchTerm
      );

      if (sectionOptions?.length) {
        sections.push(
          <div
            key={sectionName}
            className={`timezone-dropdown-item-header${
              isEmpty ? " first-header" : ""
            }`}
          >
            <p>{sectionName}</p>
          </div>
        );
        sectionOptions.forEach((option) => {
          sections.push(
            <Dropdown.Item
              eventKey={option.tzCode}
              key={`${sectionName}_${option.tzCode}`}
              className="timezone-dropdown-item"
            >
              {option.label} ({option.location})
            </Dropdown.Item>
          );
        });
        isEmpty = false;
      }
    });

    if (isEmpty) {
      return (
        <div style={{ padding: "20px" }}>
          <p className="grey-text">
            No results found. Try searching for another timezone.
          </p>
        </div>
      );
    }
    return sections;
  }, [trueSearchTerm]);

  const getDropdownMenuBody = () => {
    // if search term and trueSearchTerm are different return loading
    if (trueSearchTerm !== searchTerm) {
      return <Loading />;
    }
    return dropdownOptions;
  };

  const getToggleContent = () => {
    if (selectedValue) {
      return selectedValue;
    }
    return <p style={{ color: "#ddddde" }}>Select a timezone</p>;
  };

  return (
    <Dropdown
      show={showDropdown}
      className={getDropdownClassName()}
      onToggle={handleToggle}
      onSelect={(val) => {
        if (val) onTzCodeSelect(val);
      }}
    >
      <Dropdown.Toggle
        variant="light"
        id="dropdown-basic"
        className="timezone-dropdown-toggle"
        disabled={disabled}
      >
        {getToggleContent()}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="timezone-dropdown-menu"
        align="end"
        popperConfig={{
          modifiers: [
            {
              name: "flip",
              enabled: false, // Prevents flipping to top when there's not enough space
            },
          ],
        }}
      >
        <div>
          {getSearchableInput()}
          {getDropdownMenuBody()}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TimezoneDropdown;
